// @flow

import { type OutgoingPayment } from './types';
import URLSearchParams from '@ungap/url-search-params';
import { getApiBaseUrlByTenantCode, type Tenant } from '@tectonics/commons/tenant';
import { type IncomingPayment, normalizeIncomingPayment } from '@tectonics/prs/incoming-payment';
import { PlaceOrderError } from './place-order-error';
import { getPaymentType } from './get-payment-type';
import { isRetriableError } from './is-retriable-error';

export async function placeOrder(outgoingPayment: OutgoingPayment, tenant: Tenant, canTryToPlaceOrderAgain: boolean = true): Promise<IncomingPayment> {
  assertOutgoingCreditCardPayment(outgoingPayment);

  const response = await performConfirmRequest(outgoingPayment, tenant);

  if (response.status > 499) {
    await delay(nextDelayTime());
  }

  const responseJson = await response.json();

  if (responseJson.status === 'ERROR') {
    const normalizedResponseError = normalizeResponseError(responseJson);
    if (isRetriableError(normalizedResponseError.code) && canTryToPlaceOrderAgain) {
      // Sleeping 5 seconds before trying to tokenize again.
      await delay(5000);

      return placeOrder(outgoingPayment, tenant, false);
    }
    throw new PlaceOrderError({
      code: normalizedResponseError.code,
      message: normalizedResponseError.message,
    });
  }

  if (responseJson.status !== 'SUCCESS') throw responseJson;

  const rawIncomingPayment = responseJson.payment;
  return normalizeIncomingPayment(rawIncomingPayment);
}

function normalizeResponseError(responseJson): { code: string, message: string } {
  return {
    code: responseJson.code || responseJson.status_code,
    message: responseJson.error || responseJson.status_message,
  };
}

async function performConfirmRequest(
  outgoingPayment: OutgoingPayment,
  tenant: Tenant,
): Promise<Response> {
  const baseUrl = getApiBaseUrlByTenantCode(tenant.code);
  const path = 'ws/checkout-v2/confirm';
  const params = new URLSearchParams({ hash: outgoingPayment.hash });
  const placeOrderUrl = `${baseUrl}${path}?${params.toString()}`;

  const { customer_device_info, ...payment } = outgoingPayment;
  const body = customer_device_info ? { payment, customer_device_info } : { payment };

  return fetch(placeOrderUrl, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  });
}

function assertOutgoingCreditCardPayment(outgoingPayment: OutgoingPayment) {
  if (
    !outgoingPayment.card
    || getPaymentType(outgoingPayment) === 'google'
    || getPaymentType(outgoingPayment) === 'vouchercard'
    || getPaymentType(outgoingPayment) === 'oneclick'
  ) {
    return;
  }

  const { card } = outgoingPayment;

  const placeOrderError = new PlaceOrderError({
    code: '',
    message: 'Erro ao validar dados do cartão',
  });

  if (!card.token || !card.card_cvv) {
    throw placeOrderError;
  }

  if (Object.hasOwnProperty.call(card, 'card_number')) {
    throw placeOrderError;
  }
}

let delayTime = 0;

export function resetDelayTime() {
  delayTime = 0;
}

export function nextDelayTime(): number {
  delayTime = delayTime ? delayTime * 3 : 10000;
  return delayTime;
}

function delay(time: number): Promise<void> {
  return new Promise(resolve => {
    setTimeout(resolve, time);
  });
}
